import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Buffer } from 'buffer';
import {
    memberQueryOptions,
    useDeleteMemberMutation,
    useDsgvoDeleteMemberMutation
} from '../../api/memberQueryOptions';

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    TextField,
} from '@mui/material';

import { fetchMemberExport } from '@/api/members';
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions';
import LoadProgress from '@/components/LoadProgress';
import { exportBlob } from '@/lib/exportUtils';
import { getOrganization } from '@/store/auth/authSlice';
import { DateField } from '@mui/x-date-pickers';
import { useFlag } from '@unleash/proxy-client-react';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useSelector } from 'react-redux';

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/info',
)({
    component: MemberComponent,
})

function MemberComponent() {
    const params = Route.useParams()
    const memberQuery = useSuspenseQuery(memberQueryOptions(params.memberUuid))
    const member = memberQuery.data

    const navigate = useNavigate()
    const sessionOrganization = useSelector(getOrganization)

    const permissionsQuery = useSuspenseQuery(fetchDetailPermissionsQueryOptions('member', params.memberUuid, ['dsgvoDelete', 'delete', 'export']))
    const permissions = permissionsQuery.data

    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const handleDelete = () => {
        setDeleteDialogOpen(false);
        deleteMemberMutation.mutate(params.memberUuid)
    }
    const handleCancel = () => {
        setDeleteDialogOpen(false);
    }

    const [dsgvoDeleteDialogOpen, setDsgvoDeleteDialogOpen] = React.useState(false);
    const handleDsgvoDelete = () => {
        setDsgvoDeleteDialogOpen(false);
        dsgvoDeleteMemberMutation.mutate(params.memberUuid)
        enqueueSnackbar(t('message.dsgvoDeleted'), { variant: 'success' });
    }
    const handleDsgvoCancel = () => {
        setDsgvoDeleteDialogOpen(false);
    }

    const dsgvoDeleteMemberMutation = useDsgvoDeleteMemberMutation(params.memberUuid)
    const deleteMemberMutation = useDeleteMemberMutation(params.memberUuid, () => {
        navigate({
            from: '/member/$memberUuid', to: '/membershipsOverview', search: {
                filter: [
                    { field: "active", operator: "is", value: "true" },
                    { field: "organization.uuid", operator: "isAnyOf", value: [sessionOrganization.uuid] },
                    { field: "scoutstate", operator: "isAnyOf", value: ['MEMBER_FULL', 'MEMBER_ADD'] }
                ],
                sort: [{ field: "member.scoutId", direction: "asc" }],
                visibility: [{ field: "properties.joblist" }, { field: "properties.section" }],
            }
        })
    }, (error: Error) => {
        enqueueSnackbar(t('error.message', { message: error.message }), { variant: 'error' });
    })

    const deleteFlag = useFlag('delete_member')
    const exportFlag = useFlag('export')

    if (memberQuery.isLoading || memberQuery.isFetching) {
        return <LoadProgress />
    }
    if (memberQuery.isLoading || memberQuery.isFetching) {
        return <LoadProgress />
    }
    if (permissionsQuery.isLoading || permissionsQuery.isFetching) {
        return <LoadProgress />
    }

    const dsgvoDelete = permissions && permissions.filter(p => p.detail === 'dsgvoDelete' && p.permissions.some(a => a === 'write')).length > 0
    const deleteCondition = deleteFlag && member.permissions && member.permissions.filter(p => p === 'delete').length > 0
    const exportCondition = exportFlag && permissions && permissions.filter(p => p.detail === 'export' && p.permissions.some(a => a === 'read')).length > 0

    return (
        (<><Grid container spacing={1}>
            <Grid
                size={{
                    xs: 12,
                    md: 6,
                    lg: 4
                }}>
                <Box>
                    <DateField
                        defaultValue={
                            member.createDate ? dayjs(member.createDate) : undefined
                        }
                        slotProps={{ textField: { readOnly: true, variant: "standard" } }}
                        label={t('property.general.createDate')}
                    />
                    <TextField
                        defaultValue={member.createdBy}
                        slotProps={{ htmlInput: { readOnly: true } }}
                        label={t('property.general.createdBy')}
                    />
                    <DateField
                        defaultValue={
                            member.updateDate ? dayjs(member.updateDate) : undefined
                        }
                        slotProps={{ textField: { readOnly: true, variant: "standard" } }}
                        label={t('property.general.updateDate')}
                    />
                    <TextField
                        defaultValue={member.updatedBy}
                        slotProps={{ htmlInput: { readOnly: true } }}
                        label={t('property.general.updatedBy')}
                    />

                </Box>
            </Grid>
        </Grid>
            <Grid container spacing={1} sx={{ marginTop: 2 }}>
                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 4
                    }}>
                    {dsgvoDelete &&
                        <Button variant="contained" color="primary" onClick={() => {
                            setDsgvoDeleteDialogOpen(true);
                        }}>
                            {t('action.dsgvoDelete')}
                        </Button>
                    }
                </Grid>
            </Grid>
            <Grid container spacing={1} sx={{ marginTop: 2 }}>
                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 4
                    }}>
                    {deleteCondition &&
                        <Button variant="contained" color="error" onClick={() => {
                            setDeleteDialogOpen(true);
                        }}>
                            {t('action.deleteEntity', { entity: t('entity.member') })}
                        </Button>
                    }
                </Grid>
            </Grid>
            <Grid container spacing={1} sx={{ marginTop: 2 }}>
                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 4
                    }}>
                    {exportCondition &&
                        <Button variant="contained" onClick={() => {
                            fetchMemberExport(params.memberUuid).then((response) => {
                                const base64 = response.base64 || ''
                                // console.log(base64)
                                const raw = Buffer.from(base64, 'base64')
                                // console.log(raw)
                                const blob = new Blob([raw], {
                                    type: 'application/json',
                                });
                                exportBlob(blob, response.filename || 'export.json');
                            })
                        }}>
                            Vollexport
                        </Button>
                    }
                </Grid>
            </Grid>
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Eintrag löschen?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Das Mitglied wird unwiderruflich gelöscht. Möchten Sie fortfahren?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel}>Abbrechen</Button>
                    <Button onClick={handleDelete} autoFocus variant="contained" color="error">
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={dsgvoDeleteDialogOpen}
                onClose={handleDsgvoCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"DSGVO löschen?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Das Mitglied wird im Sinne der DGSVO gelöscht. Der Datensatz selbst bleibt aus Statistikgründen erhalten, alle Daten werden jedoch gelösch bzw. anonymisiert. Möchten Sie fortfahren?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDsgvoCancel}>Abbrechen</Button>
                    <Button onClick={handleDsgvoDelete} autoFocus variant="contained" color="error">
                        Fortfahren
                    </Button>
                </DialogActions>
            </Dialog>
        </>)
    );
}
